import { SuperVideoBackground } from './super-video-background.mjs';
import { RE_VIMEO } from 'book-of-spells';

// the segment before the hash has to be the numeric id, otherwise
// /channels/staffpicks/123456789 reads the id itself as a hash
const RE_VIMEO_UNLISTED_PATH = /\/\d+\/([^/?#\s]+)\/?\s*$/;
const RE_VIMEO_UNLISTED_QUERY = /[?&]h=([^&#]+)/;

// an unlisted vimeo video 404s without its hash, carried either as the trailing
// path segment or as ?h= - both spellings reach this one place
export function getVimeoUnlistedHash(url) {
  if (!url) return;
  const pts = url.match(RE_VIMEO_UNLISTED_PATH) || url.match(RE_VIMEO_UNLISTED_QUERY);
  if (pts) return pts[1];
}

export class VimeoBackground extends SuperVideoBackground {
  constructor(elem, params, id, uid, factoryInstance) {
    super(elem, params, id ? id.id : null, uid, 'vimeo', factoryInstance);
    if (!id) return;
    this.unlisted = id.unlisted;

    if (this.is_mobile && !this.params.mobile) return;
    this.injectScript();

    this.player = null;

    this.injectPlayer();

    this.initVimeoPlayer();
  }

  injectScript() {
    const src = 'https://player.vimeo.com/api/player.js';
    if (Object.prototype.hasOwnProperty.call(window, 'Vimeo') || document.querySelector(`script[src="${src}"]`)) return;
    const tag = document.createElement('script');
    tag.async = true;
    if (Object.prototype.hasOwnProperty.call(window, 'onVimeoIframeAPIReady') && typeof window.onVimeoIframeAPIReady === 'function') tag.addEventListener('load', () => {
      window.onVimeoIframeAPIReady();
    });
    tag.src = src;
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  }

  initVimeoPlayer() {
    if (!Object.prototype.hasOwnProperty.call(window, 'Vimeo') || this.player !== null) return;
    this.player = new Vimeo.Player(this.playerElement);
  
    this.player.on('loaded', this.onVideoPlayerReady.bind(this));
    this.player.on('ended', this.onVideoEnded.bind(this));
    this.player.on('play', this.onVideoPlay.bind(this));
    this.player.on('pause', this.onVideoPause.bind(this));
    this.player.on('bufferstart', this.onVideoBuffering.bind(this));
    this.player.on('timeupdate', this.onVideoTimeUpdate.bind(this));

    if (this.volume !== 1 && !this.muted) this.setVolume(this.volume);
  }

  generatePlayerElement() {
    const playerElement = document.createElement('iframe');
    if (this.params.title) playerElement.setAttribute('title', this.params.title);
    playerElement.setAttribute('frameborder', 0);
    playerElement.setAttribute('allow', 'autoplay; mute');
    if (this.params['lazyloading']) playerElement.setAttribute('loading', 'lazy');

    return playerElement;
  }

  generateSrcURL(id, unlisted) {
    unlisted = unlisted ? `h=${unlisted}&` : ''
    let src = `https://player.vimeo.com/video/${id}?${unlisted}background=1&controls=0`;
  
    // the live state, not the initial params: a reload after the user unmuted or
    // paused has to come back the way they left it
    if (this.muted) {
      src += '&muted=1';
    }
  
    if (!this.paused && this.params.autoplay && (this.params['always-play'] || this.isIntersecting)) {
      src += '&autoplay=1';
    }

    if (this.params.loop) {
      src += '&loop=1&autopause=0';
    }
  
    if (this.params['no-cookie']) {
      src += '&dnt=1';
    }
  
    //WARN❗️: this is a hash not a query param
    if (this.params['start-at']) {
      src += '#t=' + this.params['start-at'] + 's';
    }

    return src;
  }

  injectPlayer() {
    this.playerElement = this.generatePlayerElement();
    this.src = this.generateSrcURL(this.id, this.unlisted);
    this.playerElement.src = this.src;
    this.playerElement.id = this.uid;
    
    this.stylePlayerElement(this.playerElement);
    this.element.appendChild(this.playerElement);
    this.resize(this.playerElement);
  }

  updateState(state) {
    this.currentState = state;
    this.dispatchEvent('video-background-state-change');
  }

  /* ===== API ===== */

  setSource(url) {
    const pts = url.match(RE_VIMEO);
    if (!pts || !pts.length) return;

    const start = this.startsAfterSwap();

    this.id = pts[1];
    this.unlisted = getVimeoUnlistedHash(url);
    this.src = this.generateSrcURL(this.id, this.unlisted);
    this.resetProgress();

    // A new iframe src navigates away from the document player.js registered its
    // handlers with, so 'ended' and 'timeupdate' stop arriving and the loop with them.
    // loadVideo keeps the player; the swap is what is left when there is none, or when
    // player.js could not load the video.
    if (this.player && this.player.loadVideo) {
      // the url form: player.js takes an unlisted hash only as ?h= on a url, never beside an id
      const video = `https://vimeo.com/${this.id}` + (this.unlisted ? `?h=${this.unlisted}` : '');
      // 'loaded' fires again and onVideoPlayerReady with it - the seek to start-at, the
      // autoplay and the duration come from there; this adds what a new load drops
      this.player.loadVideo({ url: video }).then(() => {
        this.player.setLoop(this.params.loop);
        this.player.setMuted(this.muted);
        if (start) { this.player.play(); } else { this.player.pause(); }
      }).catch(() => {
        this.playerElement.src = this.src;
      });
    } else if (this.playerElement) {
      this.playerElement.src = this.src;
    }

    this.writeSourceAttributes(url);
    this.loadBackground(this.id);
  }

  onVideoPlayerReady() {
    this.mobileLowBatteryAutoplayHack();

    if (this.params['start-at']) this.seekTo(this.params['start-at']);

    if (this.params.autoplay && (this.params['always-play'] || this.isIntersecting)) {
      this.player.play();
    }

    this.player.getDuration().then((duration) => {
      this.setDuration(duration);
    });

    this.dispatchEvent('video-background-ready');
  }

  onVideoEnded() {
    this.updateState('ended');
    this.dispatchEvent('video-background-ended');
    if (this.paused || !this.params.loop) return this.pause();
    
    this.seekTo(this.params['start-at']);
    this.updateState('playing');
    this.dispatchEvent('video-background-play');
  }

  onVideoTimeUpdate(event) {
    this.currentTime = event.seconds;
    this.percentComplete = this.timeToPercentage(event.seconds);
    this.dispatchEvent('video-background-time-update');
    this.setDuration(event.duration);

    if (this.params['end-at'] && this.duration && event.seconds >= this.duration) {
      this.onVideoEnded();
    }
  }

  onVideoBuffering() {
    this.updateState('buffering');
  }

  onVideoPlay(event) {
    this.setDuration(event.duration);

    if (!this.initialPlay) {
      this.initialPlay = true;
      this.playerElement.style.opacity = 1;

      // gotta set loop manually, cause for some reason it's true by default
      this.player.setLoop(this.params.loop);

      //Hotfixing an issue that it automatically starts playing after buffering on the first load, sometimes, not always, for an unknown reason
      if (!(this.params.autoplay && (this.params['always-play'] || this.isIntersecting))) {
        return this.player.pause();
      }
    }

    const seconds = event.seconds;
    if (this.params['start-at'] && seconds < this.params['start-at']) {
      this.seekTo(this.params['start-at']);
    }

    if (this.duration && seconds >= this.duration) {
      this.seekTo(this.params['start-at']);
    }

    this.updateState('playing');
    this.dispatchEvent('video-background-play');
  }

  onVideoPause() {
    this.updateState('paused');
    this.dispatchEvent('video-background-pause');
  }

  seek(percentage) {
    this.seekTo(this.percentageToTime(percentage));
  }

  seekTo(time) {
    if (!this.player) return;
    this.player.setCurrentTime(time);
    this.dispatchEvent('video-background-seeked');
  }

  softPause() {
    if (!this.player || this.currentState === 'paused') return;
    this.player.pause();
  }

  softPlay() {
    if (!this.player || this.currentState === 'playing') return;
    this.player.play();
  }

  play() {
    if (!this.player) return;
    this.paused = false;
    
    this.player.play();
  }

  pause() {
    if (!this.player) return;
    this.paused = true;
  
    this.player.pause();
  }

  unmute() {
    if (!this.player) return;
    this.muted = false;
  
    if (!this.initialVolume) {
      this.initialVolume = true;
      this.setVolume(this.params.volume);
    }
    this.player.setMuted(false);
    this.dispatchEvent('video-background-unmute');
  }

  mute() {
    if (!this.player) return;
    this.muted = true;
  
    this.player.setMuted(true);
    this.dispatchEvent('video-background-mute');
  }

  getVolume() {
    if (!this.player) return;
    return this.player.getVolume();
  }

  setVolume(volume) {
    if (!this.player) return;
    this.volume = volume;
  
    this.player.setVolume(volume);
    this.dispatchEvent('video-background-volume-change');
  }
}
